import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Trophy,
  Calculator,
  BookOpen,
  GraduationCap,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { GOAL_OPTIONS, UserGoal } from "@/data/goals";
import Navigation from "@/components/Navigation";

const ICON_MAP = {
  Trophy,
  Calculator,
  BookOpen,
  GraduationCap,
};

const COLOR_MAP: Record<string, string> = {
  amber: "border-amber-500 bg-amber-50 dark:bg-amber-900/20",
  green: "border-green-500 bg-green-50 dark:bg-green-900/20",
  purple: "border-purple-500 bg-purple-50 dark:bg-purple-900/20",
  blue: "border-blue-500 bg-blue-50 dark:bg-blue-900/20",
};

const ICON_COLOR_MAP: Record<string, string> = {
  amber: "text-amber-600 dark:text-amber-400",
  green: "text-green-600 dark:text-green-400",
  purple: "text-purple-600 dark:text-purple-400",
  blue: "text-blue-600 dark:text-blue-400",
};

export default function OnboardingGoals() {
  const navigate = useNavigate();
  const [selectedGoal, setSelectedGoal] = useState<UserGoal | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleContinue = async () => {
    if (!selectedGoal) return;

    setIsLoading(true);
    setError(null);

    try {
      await api.saveOnboarding({ goal: selectedGoal, weekly_hours: 3 }); // Default hours, will be set on next page
      navigate("/onboarding/time");
    } catch (err: any) {
      setError(err.message || "Failed to save goal");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center p-4">
        <div className="max-w-2xl w-full space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            What's your goal?
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            We'll personalize your learning path based on your objectives
          </p>
        </div>

        {/* Goal Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {GOAL_OPTIONS.map((goal) => {
            const Icon = ICON_MAP[goal.icon];
            const isSelected = selectedGoal === goal.id;

            return (
              <Card
                key={goal.id}
                className={`cursor-pointer transition-all duration-200 hover:scale-[1.02] border-2 ${
                  isSelected
                    ? `${COLOR_MAP[goal.color]} border-2`
                    : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600"
                }`}
                onClick={() => setSelectedGoal(goal.id)}
              >
                <CardContent className="p-6 flex flex-col items-center text-center space-y-3">
                  <div
                    className={`p-3 rounded-full ${
                      isSelected
                        ? `${COLOR_MAP[goal.color]}`
                        : "bg-gray-100 dark:bg-gray-700"
                    }`}
                  >
                    <Icon
                      className={`w-8 h-8 ${
                        isSelected
                          ? ICON_COLOR_MAP[goal.color]
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    />
                  </div>
                  <h3
                    className={`text-lg font-semibold ${
                      isSelected
                        ? "text-gray-900 dark:text-white"
                        : "text-gray-700 dark:text-gray-200"
                    }`}
                  >
                    {goal.title}
                  </h3>
                  <p
                    className={`text-sm ${
                      isSelected
                        ? "text-gray-600 dark:text-gray-300"
                        : "text-gray-500 dark:text-gray-400"
                    }`}
                  >
                    {goal.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Error Message */}
        {error && (
          <div className="text-center text-red-500 text-sm">{error}</div>
        )}

        {/* Continue Button */}
        <div className="flex justify-center">
          <Button
            size="lg"
            disabled={!selectedGoal || isLoading}
            onClick={handleContinue}
            className="w-full sm:w-auto px-8 bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {isLoading ? (
              "Saving..."
            ) : (
              <>
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </div>

        {/* Progress Indicator */}
        <div className="flex justify-center gap-2">
          <div className="w-3 h-3 rounded-full bg-indigo-500"></div>
          <div className="w-3 h-3 rounded-full bg-gray-300 dark:bg-gray-600"></div>
          <div className="w-3 h-3 rounded-full bg-gray-300 dark:bg-gray-600"></div>
        </div>
        </div>
      </div>
    </>
  );
}
